/**
 * Splits a single named value across multiple cookies to bypass the browser's
 * ~4KB per-cookie size limit. Mirrors the chunking format used by the V2
 * spotlight-ui app so that cookies written here can be parsed by V2's
 * server-side reassembler in `apps/spotlight-ui/app/core/integrations/okta-auth/tokens.ts`.
 *
 * Wire format (set by setChunked / read by getChunked):
 *   <name>_0=<chunk0>; Path=/; SameSite=Lax; Secure
 *   <name>_1=<chunk1>; ...
 *   <name>_N=<chunkN>; ...
 *
 * - Each value is URL-encoded before splitting (to handle '=', ';', and UTF-8).
 * - Chunks are written in order, indexed from 0, with no gaps.
 * - On read, missing/out-of-order chunks return null (treated as "no value").
 */

export const MAX_CHUNK_BYTES = 3500;
export const CHUNK_SUFFIX = '_';

export interface CookieOptions {
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  sessionCookie?: boolean;
  maxAge?: number;
  domain?: string;
}

function writeCookie(serialized: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = serialized;
}

export function setChunked(name: string, value: string, opts: CookieOptions = {}): void {
  if (typeof document === 'undefined') return;

  removeChunked(name, opts);

  const encoded = encodeURIComponent(value);
  const attrs = buildAttributes(opts);

  if (encoded.length === 0) {
    writeCookie(`${name}${CHUNK_SUFFIX}0=; ${attrs}`);
    return;
  }

  for (let i = 0, idx = 0; i < encoded.length; i += MAX_CHUNK_BYTES, idx++) {
    const chunk = encoded.slice(i, i + MAX_CHUNK_BYTES);
    writeCookie(`${name}${CHUNK_SUFFIX}${idx}=${chunk}; ${attrs}`);
  }
}

export function getChunked(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const raw = document.cookie;
  if (!raw) return null;

  const prefix = `${name}${CHUNK_SUFFIX}`;
  const chunks: Array<{ index: number; value: string }> = [];

  for (const cookie of raw.split(/;\s*/)) {
    const eqIndex = cookie.indexOf('=');
    if (eqIndex === -1) continue;

    const key = cookie.slice(0, eqIndex);
    if (!key.startsWith(prefix)) continue;

    const indexPart = key.slice(prefix.length);
    if (!/^\d+$/.test(indexPart)) continue;

    chunks.push({
      index: Number(indexPart),
      value: cookie.slice(eqIndex + 1),
    });
  }

  if (chunks.length === 0) return null;

  chunks.sort((a, b) => a.index - b.index);

  for (let i = 0; i < chunks.length; i++) {
    if (chunks[i].index !== i) return null;
  }

  return decodeURIComponent(chunks.map(c => c.value).join(''));
}

export function removeChunked(name: string, opts: CookieOptions = {}): void {
  if (typeof document === 'undefined') return;
  const raw = document.cookie;
  if (!raw) return;
  const prefix = `${name}${CHUNK_SUFFIX}`;

  const attrs = buildAttributes({ ...opts, sessionCookie: true, maxAge: undefined });

  for (const cookie of raw.split(/;\s*/)) {
    const eqIndex = cookie.indexOf('=');
    const key = eqIndex === -1 ? cookie : cookie.slice(0, eqIndex);

    if (!key.startsWith(prefix)) continue;
    const indexPart = key.slice(prefix.length);
    if (!/^\d+$/.test(indexPart)) continue;
    writeCookie(`${key}=; ${attrs}; Max-Age=0`);
  }
}

function buildAttributes(opts: CookieOptions): string {
  const path = opts.path ?? '/';
  const secure = opts.secure ?? true;
  const sameSite = opts.sameSite ?? 'Lax';
  const parts = [`Path=${path}`, `SameSite=${sameSite}`];
  if (secure) parts.push('Secure');
  if (opts.domain) parts.push(`Domain=${opts.domain}`);
  if (!opts.sessionCookie && typeof opts.maxAge === 'number') {
    parts.push(`Max-Age=${opts.maxAge}`);
  }
  return parts.join('; ');
}
